import { Component, Input, OnChanges, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { LucideAngularModule, LucideIconData } from 'lucide-angular';
import { IconService } from './icons';

/**
 * Enterprise-grade Icon Component
 * Automatically resolves and displays Lucide icons with fallback support
 *
 * @example
 * <app-icon name="Sun" /> or <app-icon name="sun" />
 * <app-icon name="User" size="32" class="text-blue-500" />
 * <app-icon name="invalid-icon" /> <!-- Shows fallback icon -->
 */
@Component({
  selector: 'app-icon-v2',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <lucide-angular
      [img]="iconData()"
      [size]="size"
      [strokeWidth]="strokeWidth"
      [absoluteStrokeWidth]="absoluteStrokeWidth"
      [class]="customClass"
    />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        vertical-align: middle;
        line-height: 0;
      }

      :host ::ng-deep lucide-angular {
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
      }

      :host ::ng-deep lucide-angular svg {
        display: block;
      }
    `,
  ],
})
export class IconComponentV2 implements OnChanges {
  /**
   * Icon name - any valid Lucide icon name
   * Supports PascalCase, camelCase, or kebab-case (e.g., "Sun", "sun", "chevron-down")
   */
  @Input({ required: true }) name!: string;

  /**
   * Icon size in pixels
   * @default 24
   */
  @Input() size: number | string = 24;

  /**
   * Stroke width
   * @default 2
   */
  @Input() strokeWidth: number | string = 2;

  /**
   * Whether to use absolute stroke width
   * @default false
   */
  @Input() absoluteStrokeWidth: boolean = false;

  /**
   * Custom CSS classes
   */
  @Input() customClass: string = '';

  /**
   * Signal holding the icon data
   */
  iconData = signal<LucideIconData | undefined>(undefined);

  constructor(private iconService: IconService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name']) {
      this.loadIcon();
    }
  }

  /**
   * Load the icon data from the service
   */
  private loadIcon(): void {
    if (!this.name) {
      console.error('Icon name is required');
      return;
    }

    const icon = this.iconService.getIcon(this.name);
    this.iconData.set(icon);
  }
}
